import ISimpleGuest from "@/models/guests/isimple-guest";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import * as guestsService from "@/services/guests-service";
import * as tripsService from "@/services/trips-service";
import Spinner from "@/components/spinner";
import { toast } from "react-toastify";
import { isStringEmpty } from "@/common/validation-checkers";
import ViewGuestsTrips from "../trips/view";
import { useModal } from "@/components/modals/templating/modal";

interface IProps {
    compact?: boolean;
    lighterColours?: boolean;
    selectedGuest?: (guest: ISimpleGuest) => void;
}

const Search: React.FC<IProps> = ({ selectedGuest, compact = false, lighterColours = false }) => {

    const [ guests, setGuests ] = useState<ISimpleGuest[]>([]);
    const [ filteredGuests, setFilteredGuests ] = useState<ISimpleGuest[]>([]);
    const [ isLoading, setIsLoading ] = useState<boolean>(true);

    const autoFocusElement = useRef<HTMLInputElement>(null);

    const [ selectedGuestId, setSelectedGuestId ] = useState<number>(0);
    const TripsModal = useModal(<ViewGuestsTrips guestId={selectedGuestId} closeModal={() => TripsModal.closeModal()} />);

    const navigate = useNavigate();

    async function updateData(){
        try {
            const response = await guestsService.getAllGuests();

            setFilteredGuests(response);
            setGuests(response);

            setIsLoading(false);
        } catch (e) {
            toast.error((e as Error).message);

            if (e instanceof Error && (e as Error).message.includes("Unauthorised")) {
                navigate("/login");
            }

            setIsLoading(false);
        }
    }

    useEffect(() => {
        const fetchData = async () => await updateData();

        fetchData();
    }, [])

    useEffect(() => {
        if (autoFocusElement.current) {
            autoFocusElement.current.focus();
        }
    }, [autoFocusElement.current])

    function tableHeader(name: string) {
        return (
            <th className={`${lighterColours ? 'bg-gray-800' : 'bg-blue-800' }  text-lg py-2 px-3`}>
                {name}
            </th>
        )
    }

    function tableData(value: string | JSX.Element) {
        return (
            <td className="py-1 px-4">
                {value}
            </td>
        )
    }

    function search(searchTerm: string) {
        if (isStringEmpty(searchTerm)) {
            setFilteredGuests(guests);
        }
        
        const regex = new RegExp(searchTerm, "i");

        setFilteredGuests(guests.filter(x => {
            if (
                x.firstName.search(regex) !== -1 ||
                x.lastName.search(regex) !== -1  ||
                (!!x.emailAddress && x.emailAddress.search(regex) !== -1)
                ) {
                    return x;
            }
        }));
    }

    async function onSelectedGuestHandler(guest: ISimpleGuest) {
        if (selectedGuest) {
            selectedGuest(guest);
        } else {
            if (guest.trips === 1) {
                const response = await tripsService.getTrips(guest.id);
                navigate(`/view/${response[0].retreatId}`, { state: { guestId: guest.id } })
            } else {
                setSelectedGuestId(guest.id);
                TripsModal.openModal();
            }
        }
    }

    return (
        <Spinner isLoading={isLoading}>

        {!compact && (
            <>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-thin text-center">Search</h1>
                <hr className="md:w-2/3 mx-auto my-3" />
            </>
        )}
        <div className="flex justify-center text-xl mb-5">
            <input 
                ref={autoFocusElement}
                className="text-black px-2 py-1 block md:w-1/2 w-full mx-auto"
                name="Search box"
                type="text"
                placeholder="Search for someone awesome..."
                onChange={(event) => search(event.target.value)} 
            />
        </div>

        <div>
            <table className={compact ? '' : 'w-full' }>
                <thead className={compact ? `table w-full table-fixed` : '' }>
                    <tr className="border-b border-gray-800 border-dashed last:border-b-0">
                        {tableHeader("First name")}
                        {tableHeader("Last name")}
                        {tableHeader("Email address")}
                    </tr>
                </thead>
                <tbody className={compact ? `h-96 block overflow-y-auto overflow-x-hidden` : ''}>
                    {filteredGuests?.map(guest => (
                        <tr 
                            className={`${lighterColours ? 'bg-gray-600' : 'bg-br-blue-500'} ${compact ? 'table w-full table-fixed' : ''} hover:bg-br-blue-400 duration-100 text-center cursor-pointer border-t border-dashed border-gray-400 first:border-solid first:border-gray-900`}
                            key={guest.id} 
                            onClick={async () => await onSelectedGuestHandler(guest)}
                            >
                            {tableData(guest.firstName)}
                            {tableData(guest.lastName)}
                            {tableData(guest.emailAddress)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        {TripsModal.modalOpen && <TripsModal.ModalContent />}
    </Spinner>
    )
}

export default Search;